import React, { useMemo, useContext } from "react";
import orderBy from "lodash/orderBy";
import { Checkbox } from "../checkbox";
import { Virtuoso } from "react-virtuoso";
import { FiChevronDown } from "react-icons/fi";
import { cx } from "class-variance-authority";
import { get } from "lodash";
import { SortDirection } from "../utils/useSort";
import Tooltip from "../tooltip";

export interface ColumnDef<T extends object> {
  id: string;
  Header: string | ((row: T, index: number) => React.ReactNode);
  Cell: (row: T, index: number) => React.ReactNode;
  width: string;
}

interface SelectDef<T> {
  onSelect: (row: T) => void;
  onSelectAll?: () => void;
  isSelected: (row: T) => boolean | "indeterminate";
}

const NOOP_SELECT = {
  onSelect: () => {},
  onSelectAll: () => {},
  isSelected: () => false,
};

export interface SortDef<T extends object> {
  key: string;
  accessor: string | ((row: T) => unknown);
  direction: SortDirection;
}

interface TableProps<T extends object> {
  columns: ColumnDef<T>[];
  hiddenColumns?: string[];
  hideSelect?: boolean;
  data: T[];
  displayHeaders?: boolean;
  headerHeight?: number;
  rowHeight?: number;
  sort?: SortDef<T>[];
  search?: {
    accessor: (row: T) => string;
    value: string;
  };
  select?: SelectDef<T>;
  placeholder?: string;
  rowClassName?: (index: number) => string;
  headerClassName?: string;
}

export const RowBorder: React.FC<any> = ({
  rowHeight,
  children,
  className,
}) => {
  return (
    <div className={cx("border-b-dublin-gray w-full border-b", className)}>
      {children}
    </div>
  );
};

const SelectRowCell = (row: any) => {
  const { select } = useContext(TableContext);
  return (
    <div className="flex h-full items-center justify-center">
      <Checkbox
        color="magenta"
        checked={select.isSelected(row)}
        onChange={() => {
          select.onSelect(row);
        }}
      />
    </div>
  );
};

const SelectHeader = (row: any) => {
  const { select } = useContext(TableContext);
  return (
    <div className="flex h-full items-center justify-center">
      {select.onSelectAll ? (
        <Checkbox
          color="magenta"
          checked={select.isSelected(row)}
          onChange={() => {
            select.onSelectAll();
          }}
        />
      ) : null}
    </div>
  );
};

const SortIcon = (props: { direction: SortDirection | null }) => {
  const classes = ["text-magenta", "text-portland-gray"];

  return (
    <div className="text-portland-gray flex flex-col">
      <FiChevronDown
        className={cx(
          "rotate-180",
          props.direction != null
            ? classes[props.direction === "asc" ? 0 : 1]
            : null,
        )}
      />
      <FiChevronDown
        className={cx(
          props.direction != null
            ? classes[props.direction === "asc" ? 1 : 0]
            : null,
          "mt-[-4px]",
        )}
      />
    </div>
  );
};

export const BasicSortableHeader = (props: {
  label: string;
  onClick: () => void;
  sortDirection: SortDirection | null;
  className?: string;
  innerClassName?: string;
}) => {
  return (
    <Tooltip content={props.label}>
      <div
        role="button"
        onKeyDown={(e) => {
          e.preventDefault();
          if (e.key === "Enter") {
            props.onClick();
          }
        }}
        tabIndex={0}
        className={cx("flex h-full items-center space-x-3", props.className)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.onClick();
        }}
      >
        <span
          className={cx(
            "text-charcoal w-full overflow-hidden overflow-ellipsis whitespace-nowrap text-base font-medium",
            props.innerClassName,
          )}
        >
          {props.label}
        </span>
        <SortIcon direction={props.sortDirection} />
      </div>
    </Tooltip>
  );
};

export const BasicHeader = (props: {
  label: string;
  className?: string;
  innerClassName?: string;
}) => {
  return (
    <div className={cx("flex h-full items-center space-x-3", props.className)}>
      <span
        className={cx(
          "text-charcoal overflow-hidden overflow-ellipsis whitespace-nowrap text-base font-medium",
          props.innerClassName,
        )}
      >
        {props.label}
      </span>
    </div>
  );
};

const SelectableColumn: ColumnDef<any> = {
  id: "_flo-internal-column-select",
  Header: (row) => <SelectHeader {...row} />,
  Cell: (row) => <SelectRowCell {...row} />,
  width: "64px",
};

const TableContext = React.createContext<{
  select: SelectDef<unknown>;
}>({
  select: NOOP_SELECT,
});

export const Table = <T extends object>(props: TableProps<T>) => {
  const data = useMemo(() => {
    let finalData = props.data;

    if (props.search) {
      finalData = finalData.filter((row) => {
        return props?.search
          ?.accessor(row)
          ?.toLocaleUpperCase()
          ?.includes(props.search.value.toLocaleUpperCase());
      });
    }

    if (props.sort) {
      finalData = orderBy(
        finalData,
        props.sort.map((s) => s.accessor),
        props.sort.map((s) => s.direction),
      );
    }

    return finalData;
  }, [props.data, props.sort]);

  const columns = useMemo(() => {
    return [
      !props.hideSelect ? SelectableColumn : null,
      ...props.columns.filter((c) =>
        props.hiddenColumns ? props.hiddenColumns.indexOf(c.id) === -1 : true,
      ),
    ].filter(Boolean);
  }, [props.columns, props.hiddenColumns, props.hideSelect]);

  const headerHeight = get(props, "headerHeight", props.rowHeight) ?? 56;
  const rowHeight = get(props, "rowHeight", props.rowHeight) ?? 56;

  return (
    <TableContext.Provider
      value={{
        select: props.select || NOOP_SELECT,
      }}
    >
      <div className="relative h-full w-full">
        <div
          className="border-b-dublin-gray absolute z-10 w-full border-b"
          style={{
            top: headerHeight,
          }}
        />
        {data.length === 0 && props.placeholder ? (
          <div
            style={{
              paddingTop: headerHeight,
            }}
            className="absolute inset-0 z-10 flex items-center justify-center"
          >
            <p className="text-macbook">{props.placeholder}</p>
          </div>
        ) : null}
        <Virtuoso
          // Add one for the header row
          totalCount={data.length + 1}
          // Sticky the header row
          overscan={10}
          topItemCount={1}
          itemContent={(rowIndex) => {
            // Renders the header row.
            if (rowIndex === 0) {
              return (
                <div
                  className={cx("flex w-full bg-white", props.headerClassName)}
                  style={{ height: headerHeight }}
                >
                  {columns.map((column: ColumnDef<T>) => {
                    return (
                      <div
                        key={column.id}
                        className="bg-white"
                        style={{
                          width: column.width,
                          overflow: "hidden",
                          minWidth: column.width,
                          maxWidth: column.width,
                        }}
                      >
                        {typeof column.Header === "function"
                          ? column.Header(data[rowIndex], rowIndex)
                          : column.Header}
                      </div>
                    );
                  })}
                </div>
              );
            }
            return (
              <div
                className={cx(
                  "flex bg-white",
                  (props.rowClassName ?? (() => ""))(rowIndex - 1),
                )}
              >
                {columns.map((column) => {
                  return (
                    <div
                      key={column.id}
                      style={{
                        width: column.width,
                        height: rowHeight,
                        minWidth: column.width,
                        maxWidth: column.width,
                      }}
                    >
                      {column.Cell(data[rowIndex - 1], rowIndex - 1)}
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
      </div>
    </TableContext.Provider>
  );
};

export default Table;
